import { useI18n } from '@/i18n'
import { CalendarBlank, CaretLeft, CaretRight, Clock, X } from '@/shared/icons/phosphor'
import { DatePicker as Ark, type DatePickerValueChangeDetails } from '@ark-ui/solid/date-picker'
import { type Component, For, Index, type JSX, Show, createMemo, createUniqueId } from 'solid-js'
import { Portal } from 'solid-js/web'
import {
  calendarLocale,
  formatDisplay,
  joinIso,
  parseDisplay,
  splitIso,
  toDateValues,
  toIsoDate,
  todayIso,
} from './date-value'

export interface DatePickerProps {
  value?: string | undefined
  onChange?: ((value: string) => void) | undefined
  withTime?: boolean | undefined
  label?: string | undefined
  description?: string | undefined
  hint?: string | undefined
  error?: string | undefined
  placeholder?: string | undefined
  min?: string | undefined
  max?: string | undefined
  disabled?: boolean | undefined
  required?: boolean | undefined
  clearable?: boolean | undefined
  id?: string | undefined
  name?: string | undefined
  size?: 'sm' | 'md' | undefined
  class?: string | undefined
  /** Replaces the input and its calendar button. The calendar is unchanged. */
  trigger?: JSX.Element | undefined
  triggerLabel?: string | undefined
}

/* `overlap` is what lets the calendar move vertically, at the cost of covering
   the trigger. Zag's `slide` only shifts a popover sideways, so without it a
   trigger low in the window leaves the month header above y=0, where no scroll
   can reach it. `fitViewport` caps the box for a window too short for either
   placement. */
export const calendarPositioning: NonNullable<Ark.RootProps['positioning']> = {
  strategy: 'fixed',
  placement: 'bottom-start',
  gutter: 6,
  overlap: true,
  fitViewport: true,
}

const navButton = `
  flex items-center justify-center h-8 w-8
  rounded-[var(--radius-8)]
  text-[color:var(--color-text-muted)]
  transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
  hover:bg-[var(--color-neutral-100)] hover:text-[color:var(--color-text)]
  focus-visible:outline-2 focus-visible:-outline-offset-2
  focus-visible:outline-[var(--color-primary-500)]
`.trim()

const cellTrigger = `
  flex items-center justify-center w-9 h-9 text-sm
  rounded-[var(--radius-md)]
  text-[color:var(--color-text)]
  transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
  hover:bg-[var(--color-primary-100)] hover:text-[color:var(--color-primary-700)]
  data-[today]:font-semibold
  data-[today]:shadow-[inset_0_0_0_1px_var(--color-primary-300)]
  data-[selected]:bg-[var(--color-primary-500)]
  data-[selected]:text-[var(--color-on-primary)]
  data-[selected]:font-semibold
  data-[selected]:hover:bg-[var(--color-primary-600)]
  data-[outside-range]:text-[color:var(--color-text-subtle)]
  data-[outside-range]:opacity-40
  data-[disabled]:opacity-30 data-[disabled]:cursor-not-allowed
  focus-visible:outline-2 focus-visible:-outline-offset-2
  focus-visible:outline-[var(--color-primary-500)]
  data-[selected]:focus-visible:-outline-offset-4
  data-[selected]:focus-visible:outline-[var(--color-on-primary)]
`.trim()

const gridTrigger = `
  flex items-center justify-center w-full py-2 text-sm
  rounded-[var(--radius-md)]
  text-[color:var(--color-text)]
  transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
  hover:bg-[var(--color-primary-100)] hover:text-[color:var(--color-primary-700)]
  data-[selected]:bg-[var(--color-primary-500)]
  data-[selected]:text-[var(--color-on-primary)]
  data-[selected]:font-semibold
  focus-visible:outline-2 focus-visible:-outline-offset-2
  focus-visible:outline-[var(--color-primary-500)]
  data-[selected]:focus-visible:-outline-offset-4
  data-[selected]:focus-visible:outline-[var(--color-on-primary)]
`.trim()

const viewTrigger = `
  text-sm font-semibold text-[color:var(--color-text)]
  px-2 py-1 rounded-[var(--radius-md)]
  transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
  hover:bg-[var(--color-neutral-100)]
`.trim()

export const DatePicker: Component<DatePickerProps> = (props) => {
  const { t, locale } = useI18n()

  const fieldId = createMemo(() => props.id ?? `datepicker-${createUniqueId()}`)
  const parts = createMemo(() => splitIso(props.value))
  const selected = createMemo(() => toDateValues(parts().date))

  const bound = (iso: string | undefined) => {
    const values = toDateValues(splitIso(iso).date)
    return values.length > 0 ? values[0] : undefined
  }

  const emit = (date: string, time: string) => {
    props.onChange?.(joinIso(date, time, props.withTime === true))
  }

  const handleValueChange = (details: DatePickerValueChangeDetails) => {
    const next = details.value[0]
    emit(next ? toIsoDate(next) : '', parts().time)
  }

  const triggerName = createMemo(() => {
    const label = props.triggerLabel ?? props.label
    const [date] = selected()
    const value = date
      ? [formatDisplay(date, locale()), parts().time].filter(Boolean).join(' ')
      : ''
    if (label && value) {
      return t('designSystem.datePicker.labelWithValue', { label, value })
    }
    return label || value || t('designSystem.datePicker.openCalendar')
  })

  const dayLabel = (day: { formattedDate: string; selected: boolean; unavailable: boolean }) => {
    const params = { date: day.formattedDate }
    if (day.unavailable) {
      return t('designSystem.datePicker.dayUnavailable', params)
    }
    if (day.selected) {
      return t('designSystem.datePicker.daySelected', params)
    }
    return t('designSystem.datePicker.dayChoose', params)
  }

  return (
    <div class={`flex flex-col gap-1.5 ${props.class ?? ''}`}>
      <Show when={props.label}>
        <div class="flex flex-col">
          <label
            for={fieldId()}
            class="text-[length:var(--text-sm)] font-[var(--font-medium)] text-[color:var(--color-text)]"
          >
            {props.label}
          </label>
          <Show when={props.description}>
            <p class="text-[length:var(--text-xs)] text-[color:var(--color-text-muted)] mt-0.5">
              {props.description}
            </p>
          </Show>
        </div>
      </Show>

      <Ark.Root
        value={selected()}
        onValueChange={handleValueChange}
        locale={calendarLocale(locale())}
        selectionMode="single"
        fixedWeeks
        closeOnSelect={props.withTime !== true}
        disabled={props.disabled}
        format={(date) => formatDisplay(date, locale())}
        parse={(value) => parseDisplay(value, locale())}
        min={bound(props.min)}
        max={bound(props.max)}
        positioning={calendarPositioning}
      >
        <Show when={props.trigger}>
          {/* Still wrapped in Control: the positioner anchors the calendar to
              that element, so a bare Trigger leaves it pinned to the top-left
              of the viewport instead of dropping under the control. */}
          <Ark.Control class="w-fit">
            <Ark.Trigger class="outline-none" aria-label={triggerName()}>
              {props.trigger}
            </Ark.Trigger>
          </Ark.Control>
        </Show>

        <Show when={!props.trigger}>
          <Ark.Control class="relative flex items-center">
            <Ark.Input
              id={fieldId()}
              name={props.name}
              required={props.required}
              placeholder={props.placeholder ?? t('designSystem.datePicker.placeholder')}
              class={`
              ds-field w-full rounded-[var(--radius-12)] border bg-[var(--color-surface)]
              pl-3 pr-10
              ${props.size === 'sm' ? 'h-9 text-[length:var(--text-sm)]' : 'h-10 text-[length:var(--text-base)]'}
              font-[family-name:var(--font-sans)]
              text-[color:var(--color-text)]
              placeholder:text-[color:var(--color-text-subtle)]
              transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
              disabled:opacity-50 disabled:cursor-not-allowed
              ${
                props.error
                  ? 'border-[var(--color-error-600)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-error-600)]'
                  : 'border-[var(--color-field-border)] hover:not-disabled:border-[var(--color-field-hover)] focus:outline-none focus:border-[var(--color-primary-400)] focus:shadow-[var(--focus-ring)]'
              }
            `.trim()}
            />
            <Ark.Trigger
              aria-label={t('designSystem.datePicker.openCalendar')}
              class="absolute right-2 flex items-center justify-center h-7 w-7 rounded-[var(--radius-8)] text-[color:var(--color-text-muted)] transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)] hover:bg-[var(--color-neutral-100)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)] disabled:opacity-50"
            >
              <CalendarBlank class="w-4 h-4" />
            </Ark.Trigger>
          </Ark.Control>
        </Show>

        {/* Portaled on purpose. Rendering in place looks tempting, since it would
            keep the popover inside a host dialog's DOM tree, but Kobalte's
            dialog root carries a transform, which makes it the containing
            block for `position: fixed`, so the popover cannot escape the
            dialog's `overflow: hidden` and gets cut off. Portaling escapes
            both; `Dialog` in turn knows not to read a click in here as an
            outside interaction. Outside the dialog's tree, a modal Kobalte
            dialog also aria-hides the calendar and pulls focus back out of
            it; `data-kb-top-layer` is Kobalte's exemption from both. */}
        <Portal>
          {/* The z-index has to live here, not on the positioner. Zag writes
            `z-index: var(--z-index)` AND `--z-index: auto` into the
            positioner's own inline style attribute, clobbering anything we
            set there, so the positioner always computes to `z-index: auto`
            and loses to the dialog's z-50. A `position: relative` wrapper
            owns a stacking context at 60 instead, which the positioner
            inherits. `relative` (not `transform`) is deliberate: it does not
            create a containing block for the fixed-position child. */}
          <div class="relative z-[60]" data-kb-top-layer="">
            <Ark.Positioner>
              <Ark.Content
                aria-label={t('designSystem.datePicker.calendar')}
                aria-roledescription={t('designSystem.datePicker.roleDescription')}
                class={`
              w-[272px] max-w-[calc(100vw-2rem)]
              max-h-[var(--available-height)] overflow-y-auto overscroll-contain
              rounded-[var(--radius-lg)]
              border border-[var(--color-border)]
              bg-[var(--color-surface)]
              p-3
              shadow-[var(--shadow-float)]
              font-[family-name:var(--font-sans)]
              animate-[dropdown-enter_150ms_var(--ease-component)]
            `.trim()}
              >
                <Ark.View view="day">
                  <Ark.Context>
                    {(api) => (
                      <div>
                        <Ark.ViewControl class="flex items-center justify-between mb-2">
                          <Ark.PrevTrigger
                            aria-label={t('designSystem.datePicker.previousMonth')}
                            class={navButton}
                          >
                            <CaretLeft class="w-4 h-4" />
                          </Ark.PrevTrigger>
                          <Ark.ViewTrigger
                            aria-label={t('designSystem.datePicker.showMonths')}
                            class={viewTrigger}
                          >
                            <Ark.RangeText />
                          </Ark.ViewTrigger>
                          <Ark.NextTrigger
                            aria-label={t('designSystem.datePicker.nextMonth')}
                            class={navButton}
                          >
                            <CaretRight class="w-4 h-4" />
                          </Ark.NextTrigger>
                        </Ark.ViewControl>

                        <Ark.Table class="w-full border-collapse">
                          <Ark.TableHead>
                            <Ark.TableRow class="flex">
                              <For each={api().weekDays}>
                                {(weekDay) => (
                                  <Ark.TableHeader class="flex-1 w-9 text-center text-xs font-medium text-[color:var(--color-text-muted)] pb-1.5">
                                    {weekDay.narrow}
                                  </Ark.TableHeader>
                                )}
                              </For>
                            </Ark.TableRow>
                          </Ark.TableHead>
                          <Ark.TableBody>
                            <Index each={api().weeks}>
                              {(week) => (
                                <Ark.TableRow class="flex">
                                  <Index each={week()}>
                                    {(day) => (
                                      <Ark.TableCell value={day()} class="flex-1 p-0">
                                        <Ark.TableCellTrigger
                                          aria-label={dayLabel(
                                            api().getDayTableCellState({ value: day() })
                                          )}
                                          class={cellTrigger}
                                        >
                                          {day().day}
                                        </Ark.TableCellTrigger>
                                      </Ark.TableCell>
                                    )}
                                  </Index>
                                </Ark.TableRow>
                              )}
                            </Index>
                          </Ark.TableBody>
                        </Ark.Table>
                      </div>
                    )}
                  </Ark.Context>
                </Ark.View>

                <Ark.View view="month">
                  <Ark.Context>
                    {(api) => (
                      <div>
                        <Ark.ViewControl class="flex items-center justify-between mb-2">
                          <Ark.PrevTrigger
                            aria-label={t('designSystem.datePicker.previousYear')}
                            class={navButton}
                          >
                            <CaretLeft class="w-4 h-4" />
                          </Ark.PrevTrigger>
                          <Ark.ViewTrigger
                            aria-label={t('designSystem.datePicker.showYears')}
                            class={viewTrigger}
                          >
                            <Ark.RangeText />
                          </Ark.ViewTrigger>
                          <Ark.NextTrigger
                            aria-label={t('designSystem.datePicker.nextYear')}
                            class={navButton}
                          >
                            <CaretRight class="w-4 h-4" />
                          </Ark.NextTrigger>
                        </Ark.ViewControl>
                        <Ark.Table columns={4} class="w-full border-collapse">
                          <Ark.TableBody>
                            <Index each={api().getMonthsGrid({ columns: 4, format: 'short' })}>
                              {(months) => (
                                <Ark.TableRow class="flex">
                                  <Index each={months()}>
                                    {(month) => (
                                      <Ark.TableCell
                                        value={month().value}
                                        columns={4}
                                        class="flex-1 p-0.5"
                                      >
                                        <Ark.TableCellTrigger class={gridTrigger}>
                                          {month().label}
                                        </Ark.TableCellTrigger>
                                      </Ark.TableCell>
                                    )}
                                  </Index>
                                </Ark.TableRow>
                              )}
                            </Index>
                          </Ark.TableBody>
                        </Ark.Table>
                      </div>
                    )}
                  </Ark.Context>
                </Ark.View>

                <Ark.View view="year">
                  <Ark.Context>
                    {(api) => (
                      <div>
                        <Ark.ViewControl class="flex items-center justify-between mb-2">
                          <Ark.PrevTrigger
                            aria-label={t('designSystem.datePicker.previousDecade')}
                            class={navButton}
                          >
                            <CaretLeft class="w-4 h-4" />
                          </Ark.PrevTrigger>
                          <Ark.ViewTrigger
                            aria-label={t('designSystem.datePicker.showYears')}
                            class={viewTrigger}
                          >
                            <Ark.RangeText />
                          </Ark.ViewTrigger>
                          <Ark.NextTrigger
                            aria-label={t('designSystem.datePicker.nextDecade')}
                            class={navButton}
                          >
                            <CaretRight class="w-4 h-4" />
                          </Ark.NextTrigger>
                        </Ark.ViewControl>
                        <Ark.Table columns={4} class="w-full border-collapse">
                          <Ark.TableBody>
                            <Index each={api().getYearsGrid({ columns: 4 })}>
                              {(years) => (
                                <Ark.TableRow class="flex">
                                  <Index each={years()}>
                                    {(year) => (
                                      <Ark.TableCell
                                        value={year().value}
                                        columns={4}
                                        class="flex-1 p-0.5"
                                      >
                                        <Ark.TableCellTrigger class={gridTrigger}>
                                          {year().label}
                                        </Ark.TableCellTrigger>
                                      </Ark.TableCell>
                                    )}
                                  </Index>
                                </Ark.TableRow>
                              )}
                            </Index>
                          </Ark.TableBody>
                        </Ark.Table>
                      </div>
                    )}
                  </Ark.Context>
                </Ark.View>

                <Show when={props.withTime}>
                  <div class="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-[var(--color-border)]">
                    <Clock class="w-4 h-4 text-[color:var(--color-text-muted)]" />
                    <label
                      for={`${fieldId()}-time`}
                      class="text-[length:var(--text-sm)] text-[color:var(--color-text-muted)]"
                    >
                      {t('designSystem.datePicker.time')}
                    </label>
                    <input
                      id={`${fieldId()}-time`}
                      type="time"
                      value={parts().time}
                      disabled={parts().date === ''}
                      onInput={(e) => emit(parts().date, e.currentTarget.value)}
                      class="ml-auto rounded-[var(--radius-8)] border border-[var(--color-field-border)] bg-[var(--color-surface)] px-2 py-1 text-[length:var(--text-sm)] text-[color:var(--color-text)] focus:outline-none focus:border-[var(--color-primary-400)] focus:shadow-[var(--focus-ring)] disabled:opacity-50 [&::-webkit-calendar-picker-indicator]:hidden"
                    />
                  </div>
                </Show>

                <Show when={props.clearable !== false}>
                  <div class="flex items-center justify-between mt-2.5 pt-2.5 border-t border-[var(--color-border)]">
                    <button
                      type="button"
                      onClick={() => emit(todayIso(), parts().time)}
                      class="text-[length:var(--text-sm)] text-[color:var(--color-primary-500)] hover:underline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)] rounded-[var(--radius-8)] px-1.5 py-0.5"
                    >
                      {t('designSystem.datePicker.today')}
                    </button>
                    <button
                      type="button"
                      onClick={() => props.onChange?.('')}
                      class="flex items-center gap-1 text-[length:var(--text-sm)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)] rounded-[var(--radius-8)] px-1.5 py-0.5"
                    >
                      <X class="w-3.5 h-3.5" />
                      {t('designSystem.datePicker.clear')}
                    </button>
                  </div>
                </Show>
              </Ark.Content>
            </Ark.Positioner>
          </div>
        </Portal>
      </Ark.Root>

      <Show when={props.error}>
        <p class="text-[length:var(--text-sm)] text-[color:var(--color-error-600)]">
          {props.error}
        </p>
      </Show>
      <Show when={props.hint && !props.error}>
        <p class="text-[length:var(--text-sm)] text-[color:var(--color-text-muted)]">
          {props.hint}
        </p>
      </Show>
    </div>
  )
}
